"use client";
import { Flex, FlexColumn, FlexRowAlignCenter } from "@/components/Flex";
import { useRef, useState } from "react";
import { useSetEditingContextSelector } from "../hooks/useSetEditingContext";
import { SetEditInput } from "./styled/SetEditInputs";
import { themeColors } from "@/utils/themeColors";
import { TagList } from "@/features/FlashcardSetOverview/components/SetOverviewTagsDescription";
import { useCombinedTags } from "@knowt/syncing/hooks/tags/useTags";
import SetSaveHandlerBtn from "./SetSaveHandlerBtn";
import { FlashcardSet } from "@knowt/syncing/graphql/schema";
import ClassSharingPillWithHandler from "@/features/ShareSettings/ClassSharingPillWithHandler";
import { useCurrentUser } from "@knowt/syncing/hooks/user/useCurrentUser";
import SharingPillWithHandler from "@/features/ShareSettings/SharingPillWithHandler";
import Mountable from "@/components/styled/div/Mountable";
import { LastSavedTime } from "@/components/LastSavedTime";
import DelayedComponent from "@knowt/syncing/components/DelayedComponent";
import DateTimePickerPopup from "@/components/DateTimePickerPopup";
import dayjs from "dayjs";
import { spacing } from "@/utils/spacing";
import CircularRectTextButton from "@/components/CircularButton/styled/CircularRectTextButton";
import { resolveClassFlashcardSetsSWRKey } from "@knowt/syncing/hooks/flashcards/utils";
import { mutate } from "swr";

const SetTitleTagDescription = () => {
    const flashcardSet = useSetEditingContextSelector(s => s.flashcardSet) as FlashcardSet;
    const pendingUpdates = useSetEditingContextSelector(s => s.pendingUpdates);

    const titleInputRef = useRef<HTMLInputElement>(null);
    const titleInit = useRef(false);

    const { tagsValueWithMetadata } = useCombinedTags(flashcardSet);

    if (!titleInit.current && titleInputRef.current && typeof flashcardSet?.title === "string") {
        titleInit.current = true;
        titleInputRef.current.value = flashcardSet.title;
    }

    const descInputRef = useRef<HTMLInputElement>(null);
    const descInit = useRef(false);

    if (!descInit.current && descInputRef.current && typeof flashcardSet?.description === "string") {
        descInit.current = true;
        descInputRef.current.value = flashcardSet.description;
    }

    return (
        <Flex
            style={{
                gap: "2.4rem",
                backgroundColor: themeColors.neutralWhite,
                borderRadius: "2rem",
                padding: "2.4rem",
            }}>
            <FlexColumn style={{ flex: 1, gap: "1.2rem" }}>
                <p className="bodyBold2">Title</p>
                <SetEditInput
                    ref={titleInputRef}
                    defaultValue={flashcardSet?.title || ""}
                    onChange={e => {
                        pendingUpdates.current.metadata.title = e.target.value;
                    }}
                    placeholder={"Enter a title, like “Chemistry - Chapter 22”"}
                />
            </FlexColumn>

            <FlexColumn style={{ flex: 1, gap: "1.2rem" }}>
                <p className="bodyBold2">Description</p>
                <SetEditInput
                    ref={descInputRef}
                    defaultValue={flashcardSet?.description || ""}
                    onChange={e => {
                        pendingUpdates.current.metadata.description = e.target.value;
                    }}
                    placeholder={"Add a description"}
                />
                <TagList flashcardSet={flashcardSet} tags={tagsValueWithMetadata} readOnly={false} />
            </FlexColumn>
        </Flex>
    );
};

const HeaderSavingRow = () => {
    const { isTeacher } = useCurrentUser();
    const lastSaved = useSetEditingContextSelector(s => s.lastSaved);
    const updateFlashcardSetMetaData = useSetEditingContextSelector(s => s.updateFlashcardSetMetaData);
    const flashcardSet = useSetEditingContextSelector(s => s.flashcardSet) as FlashcardSet;
    const isTeacherClassSet = !!(isTeacher && flashcardSet?.classId);

    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

    return (
        <FlexRowAlignCenter
            style={{
                padding: "2rem",
                backgroundColor: themeColors.neutralWhite,
                borderRadius: "2rem",
                justifyContent: "space-between",
                marginBottom: "2rem",
                gap: spacing.MD,
                flexWrap: "wrap",
            }}>
            <h1
                className="bodyBold1"
                style={{
                    minWidth: "15rem",
                }}>
                Create flashcards below!
            </h1>
            <FlexRowAlignCenter style={{ justifyContent: "flex-end", gap: "1.2rem" }}>
                <DelayedComponent delay={100}>
                    <LastSavedTime timestamp={lastSaved} />
                </DelayedComponent>
                <Mountable mount={isTeacherClassSet}>
                    <CircularRectTextButton
                        onClick={() => setIsDatePickerOpen(true)}
                        sx={{
                            minWidth: "12rem",
                            padding: `${spacing.XS_2} ${spacing.SM}`,
                            fontWeight: "bold",
                            fontSize: "1.4rem",
                            border: `1px solid ${themeColors.neutral1}`,
                        }}>
                        {flashcardSet?.deadline
                            ? `Due: ${dayjs(flashcardSet?.deadline * 1000).format("MMM D, YYYY, h:mm A")}`
                            : "Set due date"}
                    </CircularRectTextButton>
                </Mountable>
                <Mountable mount={!!flashcardSet}>
                    {isTeacherClassSet ? (
                        <ClassSharingPillWithHandler flashcardSet={flashcardSet} />
                    ) : (
                        <SharingPillWithHandler flashcardSet={flashcardSet} />
                    )}
                </Mountable>
                <SetSaveHandlerBtn />
            </FlexRowAlignCenter>

            <Mountable mount={isDatePickerOpen}>
                <DateTimePickerPopup
                    isOpen={isDatePickerOpen}
                    onClose={() => setIsDatePickerOpen(false)}
                    onAccept={async value => {
                        const deadline = value?.unix();
                        updateFlashcardSetMetaData({
                            deadline: deadline ? deadline : null,
                        });

                        // if class flashcards set is in the cache, update the cache
                        if (flashcardSet?.classId) {
                            const classSets = await mutate(
                                resolveClassFlashcardSetsSWRKey({ classId: flashcardSet?.classId }),
                                oldData => oldData,
                                { revalidate: false }
                            );

                            if (Object.keys(classSets || {}).length === 0) {
                                await mutate(resolveClassFlashcardSetsSWRKey({ classId: flashcardSet?.classId }), {
                                    ...classSets,
                                    [flashcardSet?.flashcardSetId]: {
                                        ...classSets[flashcardSet?.flashcardSetId],
                                        deadline,
                                    },
                                });
                            }
                        }

                        setIsDatePickerOpen(false);
                    }}
                    value={flashcardSet?.deadline ? dayjs(flashcardSet?.deadline * 1000) : null}
                />
            </Mountable>
        </FlexRowAlignCenter>
    );
};

const SetEditingHeader = () => {
    return (
        <FlexColumn style={{ marginBottom: "2rem" }}>
            <HeaderSavingRow />
            <SetTitleTagDescription />
        </FlexColumn>
    );
};

export default SetEditingHeader;
